// Cube Dance & Power Towers (Douthett–Steinbach): the parsimonious graphs.
// Two chords are joined when ONE voice moves ONE semitone. Triads arrange into
// four hexatonic hexagons bridged by the four augmented triads; seventh chords
// hang off the three dim7 hubs. Click around — every hop you hear is the
// smallest move harmony can make.

import { useMemo, useState } from 'react';
import { spellNote, type PitchClass } from '@/music/notes';
import { cubeDanceGraph, powerTowersGraph, type ParsimonyNode } from '@/music/parsimony';
import { getChordPlayer } from '@/audio/player';

const spell = (pc: PitchClass) => spellNote(pc, 'flat');

function playNode(node: ParsimonyNode) {
  const root = 48 + node.rootPc;
  const up = (pc: number) => root + ((((pc - node.rootPc) % 12) + 12) % 12);
  void getChordPlayer().playMidi(node.pcs.map(up), 0.03);
}

// ---------- Cube Dance ring ----------

const SIZE = 660;
const C = SIZE / 2;
const RING = 218; // hexagon centres
const HEX_R = 62;
const AUG_R = 150; // augmented hubs sit slightly inside the ring

interface Placed {
  node: ParsimonyNode;
  x: number;
  y: number;
}

/** Hexatonic chain between the hubs: maj k → min k → maj k+8 → … (P/L order). */
function hexChain(k: number): { kind: string; rootPc: number }[] {
  const roots = [k, k, (k + 8) % 12, (k + 8) % 12, (k + 4) % 12, (k + 4) % 12];
  return roots.map((rootPc, i) => ({ kind: i % 2 === 0 ? 'maj' : 'min', rootPc }));
}

function cubeDanceLayout(nodes: ParsimonyNode[]): Map<string, Placed> {
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const placed = new Map<string, Placed>();
  for (let k = 0; k < 4; k++) {
    const hexAngle = ((90 - k * 90) * Math.PI) / 180;
    const hx = C + RING * Math.cos(hexAngle);
    const hy = C - RING * Math.sin(hexAngle);
    hexChain(k).forEach((t, i) => {
      const a = ((90 - i * 60) * Math.PI) / 180;
      const node = byKey.get(`${t.kind}:${t.rootPc}`)!;
      placed.set(node.key, {
        node,
        x: hx + HEX_R * Math.cos(a),
        y: hy - HEX_R * Math.sin(a),
      });
    });
    const augAngle = ((45 - k * 90) * Math.PI) / 180;
    const aug = byKey.get(`aug:${k}`)!;
    placed.set(aug.key, {
      node: aug,
      x: C + AUG_R * Math.cos(augAngle),
      y: C - AUG_R * Math.sin(augAngle),
    });
  }
  return placed;
}

// ---------- The exhibit ----------

export function CubeDanceExhibit() {
  const cube = useMemo(() => cubeDanceGraph(spell), []);
  const layout = useMemo(() => cubeDanceLayout(cube.nodes), [cube]);
  const [cubeAt, setCubeAt] = useState('maj:0');

  const towers = useMemo(() => powerTowersGraph(spell), []);
  const [towerAt, setTowerAt] = useState('dom7:7'); // G7
  const [towerTrail, setTowerTrail] = useState<string[]>(['dom7:7']);

  const cubeEdges = useMemo(() => {
    const out: { a: Placed; b: Placed; key: string }[] = [];
    const seen = new Set<string>();
    cube.nodes.forEach((n) => {
      cube.neighbors.get(n.key)!.forEach((m) => {
        const id = [n.key, m.key].sort().join('|');
        if (seen.has(id)) return;
        seen.add(id);
        out.push({ a: layout.get(n.key)!, b: layout.get(m.key)!, key: id });
      });
    });
    return out;
  }, [cube, layout]);

  const cubeNode = cube.nodes.find((n) => n.key === cubeAt)!;
  const cubeNeighbors = cube.neighbors.get(cubeAt)!;
  const cubeActive = new Set([cubeAt, ...cubeNeighbors.map((n) => n.key)]);

  const towerNode = towers.nodes.find((n) => n.key === towerAt)!;
  const towerNeighbors = towers.neighbors.get(towerAt)!;

  const gotoTower = (key: string) => {
    setTowerAt(key);
    setTowerTrail((t) => [...t.slice(-14), key]);
    playNode(towers.nodes.find((n) => n.key === key)!);
  };

  return (
    <div className="lab-exhibit" data-testid="lab-cube-dance">
      <p className="lab-lede">
        Two chords are joined when <strong>one voice moves one semitone</strong>. The 24 triads form
        four hexatonic hexagons, connected only through the four augmented triads. R is not shown
        because it moves a voice by a whole tone.
      </p>

      <h3>Cube Dance: triads and augmented triads</h3>
      <div className="lab-row">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="cube-dance"
          style={{ width: '100%', maxWidth: 560 }}
          role="img"
          aria-label="Cube Dance graph"
        >
          {cubeEdges.map(({ a, b, key }) => {
            const active =
              cubeActive.has(a.node.key) &&
              cubeActive.has(b.node.key) &&
              (a.node.key === cubeAt || b.node.key === cubeAt);
            return (
              <line
                key={key}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={active ? 'var(--accent)' : 'var(--hairline)'}
                strokeWidth={active ? 2.4 : 1}
                opacity={active ? 1 : 0.75}
              />
            );
          })}
          {[...layout.values()].map(({ node, x, y }) => {
            const isCurrent = node.key === cubeAt;
            const isNeighbor = cubeActive.has(node.key) && !isCurrent;
            const r = node.kind === 'aug' ? 17 : 14;
            return (
              <g
                key={node.key}
                onClick={() => {
                  setCubeAt(node.key);
                  playNode(node);
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`cube-node-${node.key}`}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={r}
                  fill={
                    isCurrent
                      ? 'var(--accent)'
                      : node.kind === 'aug'
                        ? 'var(--avoid-tint)'
                        : 'var(--surface)'
                  }
                  stroke={
                    isCurrent
                      ? 'var(--accent)'
                      : isNeighbor
                        ? 'var(--accent)'
                        : node.kind === 'aug'
                          ? 'var(--danger)'
                          : 'var(--hairline)'
                  }
                  strokeWidth={isCurrent || isNeighbor ? 2 : 1.2}
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={node.kind === 'aug' ? 12 : 11}
                  fontWeight={isCurrent ? 800 : 600}
                  fill={isCurrent ? 'var(--accent-ink)' : 'var(--ink)'}
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="lab-panel">
          <div className="lab-panel-title" data-testid="cube-current">
            {cubeNode.label}
          </div>
          <p>
            One semitone away:{' '}
            {cubeNeighbors.map((n) => (
              <button
                key={n.key}
                className="btn btn-sm lab-neighbor"
                onClick={() => {
                  setCubeAt(n.key);
                  playNode(n);
                }}
              >
                {n.label}
              </button>
            ))}
          </p>
          <p className="muted">
            A triad has three neighbours (P, L and one augmented triad). An augmented triad has six:
            three major triads and three minor triads. The three key centres of Giant Steps are the
            major triads around one augmented triad.
          </p>
        </div>
      </div>

      <h3>Power Towers: seventh chords around the diminished sevenths</h3>
      <div className="lab-row">
        <svg
          viewBox="0 0 340 340"
          className="power-towers"
          style={{ width: '100%', maxWidth: 340 }}
          role="img"
          aria-label="Power Towers neighbourhood"
        >
          {towerNeighbors.map((n, i) => {
            const a = ((90 - (i * 360) / towerNeighbors.length) * Math.PI) / 180;
            const x = 170 + 118 * Math.cos(a);
            const y = 170 - 118 * Math.sin(a);
            return (
              <g
                key={n.key}
                onClick={() => gotoTower(n.key)}
                style={{ cursor: 'pointer' }}
                data-testid={`tower-node-${n.key}`}
              >
                <line x1={170} y1={170} x2={x} y2={y} stroke="var(--hairline)" />
                {n.kind === 'dim7' ? (
                  <rect
                    x={x - 15}
                    y={y - 15}
                    width={30}
                    height={30}
                    transform={`rotate(45 ${x} ${y})`}
                    fill="var(--avoid-tint)"
                    stroke="var(--danger)"
                    strokeWidth={1.6}
                  />
                ) : (
                  <circle
                    cx={x}
                    cy={y}
                    r={19}
                    fill="var(--surface)"
                    stroke="var(--accent)"
                    strokeWidth={1.4}
                  />
                )}
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={11.5}
                  fontWeight={650}
                  fill="var(--ink)"
                >
                  {n.label}
                </text>
              </g>
            );
          })}
          <circle cx={170} cy={170} r={26} fill="var(--accent)" />
          <text
            x={170}
            y={170}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={13}
            fontWeight={800}
            fill="var(--accent-ink)"
            data-testid="tower-current"
          >
            {towerNode.label}
          </text>
        </svg>
        <div className="lab-panel">
          <p>
            Every chord one semitone from the centre chord. Click a node to move there. The diamonds
            are the three °7 chords: lower any note of one and you get one of its{' '}
            <strong>four dominants</strong> (Barry Harris&rsquo;s family); raise one and you get a
            half-diminished chord. G7 and its tritone substitute D♭7 connect through the same °7
            chord, so their 3rd and 7th do not move.
          </p>
          <p className="muted" data-testid="tower-trail">
            {towerTrail.map((k) => towers.nodes.find((n) => n.key === k)!.label).join(' → ')}
          </p>
        </div>
      </div>
    </div>
  );
}
